import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Megaphone, Plus, Pencil, Trash2, AlertCircle, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/atoms/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { Textarea } from '@/components/atoms/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import {
  usePromotions,
  useCreatePromotion,
  useUpdatePromotion,
  useDeletePromotion,
} from '@/hooks/operations/usePromotions';
import type { Promotion, PromoStatus, CreatePromotionDto } from '@/types/promotion';
import { dateStringToDate } from '@/lib/date-values';

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_dashboard/dashboard/promotions')({
  component: PromotionsPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getStatus(promo: Promotion): PromoStatus {
  const now = new Date();
  const start = parseISO(promo.startDate);
  const end = parseISO(promo.endDate);
  if (!promo.active || end < now) return 'expired';
  if (start > now) return 'upcoming';
  return 'active';
}

function formatDiscount(promo: Promotion): string {
  return promo.discountType === 'percent'
    ? `${promo.discountValue}% off`
    : `$${promo.discountValue.toFixed(2)} off`;
}

const STATUS_LABELS: Record<PromoStatus, string> = {
  active: 'Active',
  upcoming: 'Upcoming',
  expired: 'Expired',
};

const STATUS_VARIANTS: Record<PromoStatus, 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  upcoming: 'secondary',
  expired: 'outline',
};

// ─── Form schema ──────────────────────────────────────────────────────────────

/** A percent discount can take off at most the whole price; the API refuses more. */
const MAX_PERCENT_DISCOUNT = 100;

const promoSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    description: z.string(),
    discountType: z.enum(['percent', 'fixed']),
    discountValueStr: z
      .string()
      .min(1, 'Required')
      .refine((v) => !isNaN(parseFloat(v)) && parseFloat(v) > 0, 'Must be a positive number'),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    promoCode: z.string(),
  })
  .superRefine((values, ctx) => {
    if (
      values.discountType === 'percent' &&
      parseFloat(values.discountValueStr) > MAX_PERCENT_DISCOUNT
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['discountValueStr'],
        message: `A percent discount can't be more than ${MAX_PERCENT_DISCOUNT}%`,
      });
    }
  });

type PromoFormValues = z.infer<typeof promoSchema>;

/** The saved promotion as the form shows it, or a blank one for a new promotion. */
function promoFormValues(promo?: Promotion): PromoFormValues {
  if (!promo) {
    return {
      name: '',
      description: '',
      discountType: 'percent',
      discountValueStr: '10',
      startDate: '',
      endDate: '',
      promoCode: '',
    };
  }
  return {
    name: promo.name,
    description: promo.description,
    discountType: promo.discountType,
    discountValueStr: String(promo.discountValue),
    startDate: promo.startDate.slice(0, 10),
    endDate: promo.endDate.slice(0, 10),
    promoCode: promo.promoCode ?? '',
  };
}

// ─── Promo Form Dialog ────────────────────────────────────────────────────────

interface PromoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing?: Promotion;
}

function PromoDialog({ open, onOpenChange, editing }: PromoDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Promotion' : 'New Promotion'}</DialogTitle>
        </DialogHeader>
        {/* useForm reads its defaultValues once, on mount. DialogContent mounts
            this on every open, and the key ties it to one promotion, so each
            open starts from the promotion being edited (NOT-774: a form owned
            by the always-mounted dialog kept the blank values it was first
            created with). */}
        <PromoForm
          key={editing?.id ?? 'new'}
          editing={editing}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

interface PromoFormProps {
  editing?: Promotion;
  onClose: () => void;
}

function PromoForm({ editing, onClose }: PromoFormProps) {
  const createPromo = useCreatePromotion();
  const updatePromo = useUpdatePromotion();

  const form = useForm<PromoFormValues>({
    resolver: zodResolver(promoSchema),
    defaultValues: promoFormValues(editing),
  });

  function onSubmit(values: PromoFormValues) {
    const dto: CreatePromotionDto = {
      name: values.name,
      discountType: values.discountType,
      discountValue: parseFloat(values.discountValueStr),
      startDate: values.startDate,
      endDate: values.endDate,
      // Optional: blank is saved as no description, and clears one on edit.
      description: values.description.trim(),
      promoCode: values.promoCode || undefined,
    };

    if (editing) {
      // An emptied promo code is removed, not left as it was.
      updatePromo.mutate(
        { ...dto, id: editing.id, promoCode: values.promoCode || null },
        { onSuccess: onClose },
      );
    } else {
      createPromo.mutate(dto, { onSuccess: onClose });
    }
  }

  const isPending = createPromo.isPending || updatePromo.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Summer Sale" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea placeholder="Optional description…" rows={2} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="discountType"
            // The 100% ceiling belongs to the type, so switching it
            // re-checks the value already entered.
            rules={{ deps: ['discountValueStr'] }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="percent">Percent (%)</SelectItem>
                    <SelectItem value="fixed">Fixed ($)</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="discountValueStr"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Discount value</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step={0.01} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="promoCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Promo code (optional)</FormLabel>
              <FormControl>
                <Input placeholder="SAVE20" className="uppercase" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving…' : editing ? 'Save changes' : 'Create'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="mt-1 h-4 w-56" />
      </CardHeader>
      <CardContent className="space-y-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-4 w-20" />
      </CardContent>
    </Card>
  );
}

// ─── Promotion card ───────────────────────────────────────────────────────────

interface PromoCardProps {
  promo: Promotion;
  onEdit: (promo: Promotion) => void;
  onDelete: (id: string) => void;
}

function PromoCard({ promo, onEdit, onDelete }: PromoCardProps) {
  const status = getStatus(promo);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{promo.name}</CardTitle>
          <div className="flex items-center gap-1 shrink-0">
            <Badge variant={STATUS_VARIANTS[status]}>{STATUS_LABELS[status]}</Badge>
          </div>
        </div>
        {promo.description && (
          <CardDescription className="line-clamp-2">{promo.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-primary">{formatDiscount(promo)}</span>
          {promo.promoCode && (
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {promo.promoCode}
            </span>
          )}
        </div>

        <div className="text-muted-foreground text-xs">
          {format(dateStringToDate(promo.startDate)!, 'MMM d, yyyy')} –{' '}
          {format(dateStringToDate(promo.endDate)!, 'MMM d, yyyy')}
        </div>

        {(promo.mentionCount > 0 || promo.conversionCount > 0) && (
          <div className="flex gap-4 text-xs">
            <span className="text-muted-foreground">
              {promo.mentionCount} mention{promo.mentionCount !== 1 ? 's' : ''}
            </span>
            <span className="text-muted-foreground">
              {promo.conversionCount} conversion{promo.conversionCount !== 1 ? 's' : ''}{' '}
              {promo.conversionRate > 0 && (
                <span>({(promo.conversionRate * 100).toFixed(0)}%)</span>
              )}
            </span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Edit ${promo.name}`}
            onClick={() => onEdit(promo)}
          >
            <Pencil className="size-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onDelete(promo.id)}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function PromotionsPage() {
  const [statusFilter, setStatusFilter] = useState<PromoStatus | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Promotion | undefined>();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const params = statusFilter !== 'all' ? { status: statusFilter } : {};
  const { data: promotions = [], isLoading, isError, refetch } = usePromotions(params);
  const deletePromo = useDeletePromotion();

  function handleEdit(promo: Promotion) {
    setEditing(promo);
    setDialogOpen(true);
  }

  // `editing` stays set while the dialog animates closed, so it keeps showing
  // the promotion it was opened for; "New promotion" clears it.
  function handleDialogClose(open: boolean) {
    setDialogOpen(open);
  }

  function handleDeleteConfirm() {
    if (!deletingId) return;
    deletePromo.mutate(deletingId, { onSuccess: () => setDeletingId(null) });
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone className="text-primary size-6" />
          <div>
            <h1 className="text-2xl font-semibold">Promotions</h1>
            <p className="text-muted-foreground text-sm">
              Manage discounts and special offers shown to customers
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isLoading}>
            <RefreshCw className={`mr-2 size-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setEditing(undefined);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-2 size-4" />
            New promotion
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="upcoming">Upcoming</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isError ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <AlertCircle className="text-destructive size-8" />
          <p className="text-muted-foreground text-sm">Failed to load promotions</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          ) : promotions.length === 0 ? (
            <div className="col-span-full py-16 text-center">
              <Megaphone className="text-muted-foreground mx-auto mb-3 size-10" />
              <p className="text-muted-foreground text-sm">
                No promotions found. Create one to get started.
              </p>
            </div>
          ) : (
            promotions.map((promo) => (
              <PromoCard
                key={promo.id}
                promo={promo}
                onEdit={handleEdit}
                onDelete={setDeletingId}
              />
            ))
          )}
        </div>
      )}

      {/* Create/Edit dialog */}
      <PromoDialog open={dialogOpen} onOpenChange={handleDialogClose} editing={editing} />

      {/* Delete confirmation */}
      <AlertDialog open={Boolean(deletingId)} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete promotion?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The promotion will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleDeleteConfirm}
              disabled={deletePromo.isPending}
            >
              {deletePromo.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
